import { useCallback, useState } from 'react'

export type PendingCreation = { start: Date; end: Date; resourceId: string }

type CreateAppointment = (
  start: Date,
  end: Date,
  resourceId: string,
  awayConfirmed?: boolean,
) => void

type PendingAwayCreation = {
  pendingCreation: PendingCreation | null
  // Holds a slot in the doctor's away time until the user answers "Doctor is away".
  requestCreation: (slot: PendingCreation, isAway: boolean) => void
  cancelCreation: () => void
  confirmCreation: () => void
}

export const usePendingAwayCreation = (
  createCustomAppointment?: CreateAppointment,
): PendingAwayCreation => {
  const [pendingCreation, setPendingCreation] = useState<PendingCreation | null>(null)

  const requestCreation = useCallback(
    (slot: PendingCreation, isAway: boolean) => {
      if (isAway) {
        setPendingCreation(slot)
        return
      }
      createCustomAppointment?.(slot.start, slot.end, slot.resourceId)
    },
    [createCustomAppointment],
  )

  const cancelCreation = useCallback(() => setPendingCreation(null), [])

  const confirmCreation = useCallback(() => {
    if (!pendingCreation) return
    setPendingCreation(null)
    // Tells the dialog the user already agreed, so Save does not ask again.
    createCustomAppointment?.(pendingCreation.start, pendingCreation.end, pendingCreation.resourceId, true)
  }, [pendingCreation, createCustomAppointment])

  return { pendingCreation, requestCreation, cancelCreation, confirmCreation }
}
